import { zodResolver } from "@hookform/resolvers/zod";
import { useClient, useGoal, useProposal, useSipData } from "creso-core/hooks";
import {
  calculateFutureValue,
  calculateMonthlySIP,
  getProposalStepUp,
} from "creso-core/utils";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { V2Loader } from "web-design-system";
import { addMonths, differenceInCalendarMonths, isToday } from "date-fns";
import { ProposalPreview } from "@/modules/reports/components/proposal/proposal-preview-dialog";
import { STEP_CONFIGS } from "@planning/config";
import { Client, Goal, Proposal } from "creso-core/types";
import {
  ProposalFormSchema,
  ProposalProvider,
  ProposalUIState,
  useProposalBuilderContext,
} from "../../providers/proposal-provider";
import { ProposalHeader } from "./proposal-header";
import { ProposalBody } from "./proposal-body";
import { ProposalFooter } from "./proposal-footer";
import { ProposalSteps } from "./proposal-steps";
import { ProposalBacktest } from "./proposal-backtest";

interface Props {
  proposalId?: number;
  goalId?: number;
  clientId?: number;
  state?: ProposalUIState;
  collectionId?: number;
}

export function ProposalBuilder(props: Props) {
  const { data: client, isLoading: isLoadingClient } = useClient(
    props.clientId as number,
    !!props.clientId
  );

  const { data: proposal, isLoading: isLoadingProposal } = useProposal(
    {
      proposalId: props.proposalId as number,
    },
    { enabled: !!props.proposalId }
  );

  const { data: goal, isLoading: isLoadingGoal } = useGoal(
    {
      id: (props.goalId as number) || (proposal?.goal as number),
    },
    { enabled: !!props.goalId || !!proposal?.goal }
  );

  const isLoading = isLoadingClient || isLoadingGoal || isLoadingProposal;

  return (
    <V2Loader isLoading={isLoading} className="flex flex-row size-full">
      <ProposalBuilderWrapper
        goal={goal}
        client={client as Client}
        proposal={proposal}
        {...props}
      />
    </V2Loader>
  );
}

function ProposalBuilderWrapper({
  client,
  proposal,
  goal,
  state,
  collectionId,
}: {
  client: Client;
  proposal?: Proposal;
  goal?: Goal;
  state?: ProposalUIState;
  collectionId?: number;
}) {
  const { data: sipData } = useSipData(
    client?.investment_account || "",
    1,
    100
  );
  // Infer if linked holdings were included for saved proposals (default: true)
  const inferredIncludeLinkedHoldings = useMemo(() => {
    // Default to true for new proposals
    if (!proposal || !goal?.id) return true;

    const linkedHoldingsValue = goal.total_current_value ?? 0;
    // Default to true if no linked holdings
    if (linkedHoldingsValue <= 0) return true;

    const goalValue = calculateFutureValue(
      Number(goal.target_corpus),
      Number(goal.expected_inflation),
      goal.duration_months
    );

    const expectedReturn = Number(proposal.expected_return || 12);
    const lumpsumAmount = Number(proposal.total_lumpsum_amount || 0);
    const savedSipAmount = Number(proposal.total_sip_amount);
    const stepUp = getProposalStepUp(
      proposal,
      proposal.proposal_funds.filter((f) => f.type === "SIP").length
    );

    // Calculate SIP without including linked holdings (higher SIP)
    const sipWithoutInclusion = calculateMonthlySIP(
      goalValue,
      expectedReturn,
      goal.duration_months,
      lumpsumAmount,
      stepUp
    );

    // Calculate SIP with including linked holdings (lower SIP)
    const sipWithInclusion = calculateMonthlySIP(
      goalValue - linkedHoldingsValue,
      expectedReturn,
      goal.duration_months,
      lumpsumAmount,
      stepUp
    );

    // Determine which calculation the saved SIP is closer to
    const diffWithoutInclusion = Math.abs(savedSipAmount - sipWithoutInclusion);
    const diffWithInclusion = Math.abs(savedSipAmount - sipWithInclusion);

    return diffWithInclusion < diffWithoutInclusion;
  }, [proposal, goal]);

  const linkedGoal = useMemo(() => {
    if (!goal?.id) return null;
    return {
      id: goal?.id as number,
      name: goal?.name as string,
      target_corpus: goal?.target_corpus as string,
      expected_inflation: goal?.expected_inflation as string,
      duration_months: goal?.duration_months || 0,
      linked_holdings: goal?.linked_holdings?.map((h) => ({
        scheme_isin: h.scheme_isin,
        folio_id: h.folio_id,
        folio_number: h.folio_number,
      })),
      category: goal?.category as string,
      total_current_value: goal.total_current_value,
    };
  }, [goal]);

  const defaultFunds = useMemo(() => {
    // Return propopsal funds if configured already.
    if (proposal?.proposal_funds.length) return proposal.proposal_funds;
    // Retunr if there are no linked goals
    if (!goal?.linked_holdings.length) return [];
    if (!sipData?.results.length) return [];

    // Check if there are ongoing SIP that contribute to the linked holdings
    // and make them the default SIP proposal funds.
    const relevantSips = sipData.results
      .filter((s) => s.status === "ACTIVE")
      .filter((s) =>
        goal?.linked_holdings.some(
          (h) => h.scheme_isin === s.scheme_isin && h.folio_id === s.folio_id
        )
      );
    return relevantSips.map((h) => ({
      fund_scheme: h.fund_scheme_id || h.scheme_id,
      source: "EXISTING" as const,
      source_folio: h.folio_id,
      type: "SIP" as const,
      sip_amount: h.installment_amount,
      lumpsum_amount: "0",
      remarks: "",
      amc_code: h.amc_morningstar_code,
      fund_scheme_name: h.scheme_name,
      fund_scheme_isin: h.scheme_isin,
    }));
  }, [proposal, goal?.linked_holdings, sipData]);

  const defaultStepUp = useMemo(
    () => ({
      step_up_amount: proposal?.step_up_amount
        ? Number(proposal.step_up_amount)
        : null,
      step_up_percentage: proposal?.step_up_percentage
        ? Number(proposal.step_up_percentage)
        : null,
      step_up_frequency: proposal?.step_up_frequency ?? null,
    }),
    [proposal]
  );

  const endDate = useMemo(() => {
    if (!goal) return new Date();
    const createdAt = new Date(goal?.created_at);
    return addMonths(createdAt, goal?.duration_months || 0);
  }, [goal]);

  const defaultProposalDuration = useMemo(() => {
    if (!proposal?.goal && proposal?.duration_months)
      return proposal?.duration_months;
    if (isToday(endDate)) return 12;
    return differenceInCalendarMonths(endDate, new Date());
  }, [endDate, proposal]);

  const defaultSipAmount = useMemo(() => {
    if (proposal) return proposal?.total_sip_amount;
    if (!goal) return "0";
    const goalFutureValue = calculateFutureValue(
      Number(goal.target_corpus),
      Number(goal.expected_inflation),
      goal.duration_months
    );
    const linkedHoldingsValue = goal.total_current_value ?? 0;
    return calculateMonthlySIP(
      goalFutureValue - linkedHoldingsValue,
      12,
      defaultProposalDuration
    ).toString();
  }, [proposal, goal, defaultProposalDuration]);

  const form = useForm({
    resolver: zodResolver(ProposalFormSchema),
    values: {
      id: proposal?.id,
      name:
        proposal?.name ||
        (goal ? `Proposal for ${goal?.name}` : `Proposal for wealth creation`),
      expected_return: proposal?.expected_return || "12",
      duration_months: defaultProposalDuration,
      client: proposal?.client || client?.id,
      goal: linkedGoal,
      include_linked_holdings: inferredIncludeLinkedHoldings,
      projected_value: proposal?.projected_value ?? null,
      total_sip_amount: defaultSipAmount,
      total_lumpsum_amount: proposal?.total_lumpsum_amount || "0",
      proposal_funds: defaultFunds,
      include_remarks: proposal?.include_remarks ?? true,
      ...defaultStepUp,
    },
  });

  const defaultState = useMemo(() => {
    if (state) return state;
    const formValues = form.getValues();
    const defaultStep = STEP_CONFIGS.filter((s) => s.show?.(formValues)).find(
      (s) => !s.isComplete?.(formValues)
    );
    if (defaultStep) {
      const { subSteps } = defaultStep;
      if (!subSteps?.length) {
        return defaultStep.id || "init";
      }
      const sStep = subSteps
        ?.filter((s) => s.show?.(formValues))
        .find((s) => s.isComplete?.(formValues));
      return sStep?.id || "init";
    }
    return "init";
  }, [state, form]);

  return (
    <ProposalProvider
      state={defaultState as ProposalUIState}
      collectionId={collectionId}
      form={form}
    >
      <ProposalBuilderContent proposal={proposal} client={client} />
    </ProposalProvider>
  );
}

function ProposalBuilderContent(props: {
  proposal?: Proposal;
  client: Client;
}) {
  const { proposal, client } = props;

  const { uiState } = useProposalBuilderContext();

  if (uiState === "backtest") {
    return <ProposalBacktest />;
  }

  if (uiState === "download") {
    if (!proposal?.id || !client?.id) return null;
    return <ProposalPreview proposalId={proposal?.id} clientId={client?.id} />;
  }

  return (
    <div className="flex flex-row size-full bg-card">
      <div className="w-[24vw] xl:w-[20vw] max-w-1/2 shrink-0 bg-card py-6 flex flex-col gap-4 px-4">
        <span className="font-semibold text-lg">Create Proposal</span>
        <ProposalSteps />
      </div>
      <div className="p-2 h-full w-full">
        <div className="flex flex-col flex-1 min-h-0 min-w-0 bg-background border border-border rounded-lg h-full overflow-hidden">
          <ProposalHeader />
          <ProposalBody />
          <ProposalFooter />
        </div>
      </div>
    </div>
  );
}
